import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import ActionsDialog from '../../../components/ActionsDialog/ActionsDialog';
import { TipShareFormData, DistributionType, AutoDistributionMethod } from '../types';
import {
  radioGroupContainerSx,
  nestedRadioGroupSx,
  rowFlexWithCenterAlignmentNoGapSx,
  rowFlexWithCenterAlignmentSmallGapSx,
  switchToAutoDialogSx,
} from '../styles';

interface TipShareDistributionFormProps {
  isReadOnly?: boolean;
}

export default function TipShareDistributionForm({ isReadOnly = false }: TipShareDistributionFormProps) {
  const { t } = useTranslation();
  const { setValue, control, formState } = useFormContext<TipShareFormData>();
  const [showSwitchToAutoDialog, setShowSwitchToAutoDialog] = useState(false);
  const [pendingAutomaticAction, setPendingAutomaticAction] = useState<(() => void) | null>(null);

  const savedDistributionType = formState.defaultValues?.distributionType;

  // Watch form values using useWatch (array destructuring)
  const [distributionType, distributionMethod, recalculateOnPosImport, autoBalanceGridDistribution] = useWatch({
    control,
    name: ['distributionType', 'distributionMethod', 'recalculateOnPosImport', 'autoBalanceGridDistribution'],
  });

  // Helper function to set multiple form values efficiently
  const setMultipleValues = useCallback(
    (updates: Array<{ name: keyof TipShareFormData; value: string | boolean | number }>) => {
      updates.forEach(({ name, value }) => {
        setValue(name, value as never, { shouldDirty: true });
      });
    },
    [setValue]
  );

  // Store a pending action and open the confirmation dialog.
  // Only show the popup if the SAVED (API) state is MANUAL — meaning real backend data
  // would be lost. If the user just unsaved-toggled to manual, no data is at risk.
  const requestSwitchToAutomatic = useCallback(
    (action: () => void) => {
      if (savedDistributionType === DistributionType.MANUAL) {
        setPendingAutomaticAction(() => action);
        setShowSwitchToAutoDialog(true);
      } else {
        // Saved state is not MANUAL — switch directly without confirmation
        action();
      }
    },
    [savedDistributionType]
  );

  // Handle distribution type change and reset related fields
  const handleDistributionTypeChange = useCallback(
    (newType: DistributionType) => {
      if (newType === DistributionType.AUTOMATIC) {
        if (distributionType === DistributionType.MANUAL) {
          // Show confirmation dialog before switching from manual to automatic
          requestSwitchToAutomatic(() => {
            setMultipleValues([
              { name: 'distributionType', value: DistributionType.AUTOMATIC },
              { name: 'autoBalanceGridDistribution', value: false },
            ]);
          });
          return;
        }
        // Set distribution type and reset manual distribution fields
        setMultipleValues([
          { name: 'distributionType', value: newType },
          { name: 'autoBalanceGridDistribution', value: false },
        ]);
      } else if (newType === DistributionType.MANUAL) {
        // Set distribution type and reset automatic distribution fields
        setMultipleValues([
          { name: 'distributionType', value: newType },
          { name: 'distributionMethod', value: '' },
          { name: 'recalculateOnPosImport', value: false },
        ]);
      }
    },
    [distributionType, requestSwitchToAutomatic, setMultipleValues]
  );

  // Switches to Automatic distribution, prepending the base automatic fields before any
  // caller-supplied fields. If the current saved state is MANUAL, shows a confirmation
  // dialog first; otherwise applies the change immediately.
  const applyAutomaticSwitch = useCallback(
    (additionalUpdates: Array<{ name: keyof TipShareFormData; value: string | boolean | number }>) => {
      const updates = [
        { name: 'distributionType' as const, value: DistributionType.AUTOMATIC },
        { name: 'autoBalanceGridDistribution' as const, value: false as boolean | string | number },
        ...additionalUpdates,
      ];
      if (distributionType === DistributionType.MANUAL) {
        requestSwitchToAutomatic(() => setMultipleValues(updates));
      } else {
        setMultipleValues(updates);
      }
    },
    [distributionType, requestSwitchToAutomatic, setMultipleValues]
  );

  // Handle distribution method change (By Job / By Labor Group)
  const handleDistributionMethodChange = useCallback(
    (method: AutoDistributionMethod) => {
      if (distributionType === DistributionType.AUTOMATIC) {
        setValue('distributionMethod', method, { shouldDirty: true });
      } else {
        applyAutomaticSwitch([{ name: 'distributionMethod', value: method }]);
      }
    },
    [distributionType, applyAutomaticSwitch, setValue]
  );

  // Handle recalculate checkbox change
  const handleRecalculateChange = useCallback(
    (checked: boolean) => {
      if (distributionType === DistributionType.AUTOMATIC) {
        setValue('recalculateOnPosImport', checked, { shouldDirty: true });
      } else {
        applyAutomaticSwitch([{ name: 'recalculateOnPosImport', value: checked }]);
      }
    },
    [distributionType, applyAutomaticSwitch, setValue]
  );

  // Handle auto balance checkbox change
  const handleAutoBalanceChange = useCallback(
    (checked: boolean) => {
      // Switch to Manual if not already selected
      if (distributionType !== DistributionType.MANUAL) {
        setMultipleValues([
          { name: 'distributionType', value: DistributionType.MANUAL },
          { name: 'distributionMethod', value: '' },
          { name: 'recalculateOnPosImport', value: false },
          { name: 'autoBalanceGridDistribution', value: checked },
        ]);
      } else {
        setValue('autoBalanceGridDistribution', checked, { shouldDirty: true });
      }
    },
    [distributionType, setMultipleValues, setValue]
  );

  return (
    <Box data-testid='tip-share-distribution-form'>
      <ActionsDialog
        open={showSwitchToAutoDialog}
        onClose={() => {
          setShowSwitchToAutoDialog(false);
          setPendingAutomaticAction(null);
        }}
        dataTestId='switch-to-auto-dialog'
        dialogTitle={t('tipShare.distribution.switchToAutoDialog.title')}
        dialogContentText={t('tipShare.distribution.switchToAutoDialog.content')}
        closeButtonText={t('buttonText.cancel')}
        closeButtonVariant='text'
        actionButtonText={t('tipShare.distribution.switchToAutoDialog.confirmButton')}
        actionButtonVariant='contained'
        actionButtonColor='error'
        onActionClick={() => {
          if (pendingAutomaticAction) pendingAutomaticAction();
          setPendingAutomaticAction(null);
          setShowSwitchToAutoDialog(false);
        }}
        dialogSx={switchToAutoDialogSx}
      />
      <RadioGroup
        name='distributionType'
        value={distributionType}
        onChange={(e) => handleDistributionTypeChange(e.target.value as DistributionType)}
        sx={radioGroupContainerSx}
      >
        {/* Distribute automatically */}
        <Box>
          <Box sx={rowFlexWithCenterAlignmentNoGapSx}>
            <FormControlLabel
              value={DistributionType.AUTOMATIC}
              control={<Radio data-testid='automatic-distribution-radio' disabled={isReadOnly} />}
              label={t('tipShare.distribution.distributeAutomatically')}
            />
            <Tooltip title={t('tipShare.distribution.automaticDistributionTooltip')} arrow placement='right'>
              <InfoIcon fontSize='small' color='action' />
            </Tooltip>
          </Box>

          {/* Nested options for automatic distribution - always visible */}
          <Box sx={nestedRadioGroupSx}>
            <RadioGroup
              name='distributionMethod'
              value={distributionMethod}
              onChange={(e) => handleDistributionMethodChange(e.target.value as AutoDistributionMethod)}
            >
              <FormControlLabel
                value={AutoDistributionMethod.BY_JOB}
                control={<Radio data-testid='by-job-radio' disabled={isReadOnly} />}
                label={t('tipShare.distribution.byJob')}
              />
              <FormControlLabel
                value={AutoDistributionMethod.BY_LABOR_GROUP}
                control={<Radio data-testid='by-labor-group-radio' disabled={isReadOnly} />}
                label={t('tipShare.distribution.byLaborGroup')}
              />
            </RadioGroup>

            {/* Recalculate checkbox */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={recalculateOnPosImport}
                    onChange={(e) => handleRecalculateChange(e.target.checked)}
                    disabled={isReadOnly}
                    data-testid='recalculate-checkbox'
                  />
                }
                label={<Typography variant='body1'>{t('tipShare.distribution.recalculateOnPosImport')}</Typography>}
              />
            </Box>
          </Box>
        </Box>

        {/* Distribute manually */}
        <Box>
          <Box sx={rowFlexWithCenterAlignmentSmallGapSx}>
            <FormControlLabel
              value={DistributionType.MANUAL}
              control={<Radio data-testid='manual-distribution-radio' disabled={isReadOnly} />}
              label={t('tipShare.distribution.distributeManually')}
            />
            <Tooltip title={t('tipShare.distribution.manualDistributionTooltip')} arrow placement='right'>
              <InfoIcon fontSize='small' color='action' />
            </Tooltip>
          </Box>

          {/* Auto Balance Grid Distribution checkbox - always visible */}
          <Box sx={nestedRadioGroupSx}>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoBalanceGridDistribution}
                    onChange={(e) => handleAutoBalanceChange(e.target.checked)}
                    disabled={isReadOnly}
                    data-testid='auto-balance-grid-distribution-checkbox'
                  />
                }
                label={
                  <Typography variant='body1'>{t('tipShare.distribution.autoBalanceGridDistribution')}</Typography>
                }
              />
            </Box>
          </Box>
        </Box>
      </RadioGroup>
    </Box>
  );
}

TipShareDistributionForm.defaultProps = {
  isReadOnly: false,
};
